import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import { VotemateLogo } from '../votemate-logo/VotemateLogo';
import AutoCompleteAddressInput from './AutoCompleteAddressInput';
import { getSignupStyles } from './Signup.styles';
import { useNotification } from '../ui-library/NotificationProvider';
import { useAuth } from '../auth/AuthProvider';
import useApi from '@/hooks/useApi';
import useAnalytics from '@/hooks/useAnalytics';
import { useResearch } from '@/contexts/ResearchContext';
import { useProfileForm } from '@/hooks/useProfileForm';
import useGuestAddress, { isCountyPreview } from '@/hooks/useGuestAddress';
import { gradientBorderBackground } from '@/theme/utils';
import { semanticColors, semanticGradients, BORDER_RADIUS } from '@/theme/tokens';
import GradientCircularProgress from '@/ui-library/GradientCircularProgress';
import { EXTERNAL_URLS } from '@/constants/urls';
import {
  DEFAULT_LANGUAGE,
  resolveSupportedLanguage,
  SupportedLanguageCode,
} from '@/constants/languages';
import i18n, { changeLanguage } from '../../i18n';
import LanguageSelector from '@/components/language-selector/LanguageSelector';
import { scrollIntoViewWithin } from '@/utils/scrollWithin';
import AnswerStyleSignupStep from '@/components/answerStyle/AnswerStyleSignupStep';
import { answerStyleStore } from '@/hooks/useAnswerStyle';
import { useOpenMembership } from '@/hooks/useOpenMembership';

interface AddRegisteredAddressProps {
  onSignupComplete: () => void;
  onGoBack: () => void;
}

// Best-effort split of an OIDC `name` claim into first/last when given_name/family_name
// aren't present (some social IdPs only return the combined name). When the IdP fills
// `name` with the email (Apple does this when the user blocked name sharing), we return
// empty so we don't pre-fill the first-name field with an email address.
const splitName = (full?: string): { first: string; last: string } => {
  const trimmed = (full || '').trim();
  if (!trimmed || trimmed.includes('@')) return { first: '', last: '' };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
};

const AddRegisteredAddress: React.FC<AddRegisteredAddressProps> = ({
  onSignupComplete,
  onGoBack,
}) => {
  const { t } = useTranslation('signup');
  const { user, refreshUserProfile, updateUser } = useAuth();
  // Set once the account exists and the optional answer-style step is showing;
  // holds the call that finishes sign-up.
  const [answerStyleStepDone, setAnswerStyleStepDone] = useState<(() => void) | null>(null);
  const { user: auth0User } = useAuth0();
  const oidcSplit = splitName(auth0User?.name);
  const [isSaving, setIsSaving] = useState(false);
  const [wantsMembership, setWantsMembership] = useState(false);
  const openMembership = useOpenMembership();
  const [firstName, setFirstName] = useState(auth0User?.given_name || oidcSplit.first || '');
  const [lastName, setLastName] = useState(auth0User?.family_name || oidcSplit.last || '');
  const initialLanguage: SupportedLanguageCode =
    resolveSupportedLanguage(i18n.language) ?? DEFAULT_LANGUAGE;
  const [language, setLanguage] = useState<SupportedLanguageCode>(initialLanguage);
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  // Refs to each field's input so a failed submit can move focus to the first
  // invalid field — the phone field in particular can be below the fold, leaving
  // the user unsure why Sign Up did nothing (#1589).
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const addressRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const focusInvalidField = (ref: React.RefObject<HTMLInputElement>) => {
    const el = ref.current;
    if (!el) return;
    // ``preventScroll`` plus a container-scoped scroll keeps the move inside the
    // widget; both a bare ``focus()`` and ``scrollIntoView`` reach past the
    // iframe and scroll the partner's page instead.
    el.focus({ preventScroll: true });
    scrollIntoViewWithin(el, { block: 'center' });
  };
  const { showNotification } = useNotification();
  const query = useApi();
  const analytics = useAnalytics();
  const { campaignCode: researchCampaignCode, participantId: researchParticipantId } =
    useResearch();
  const {
    handleAddressChange,
    handleAddressSelect,
    handlePhoneChange,
    handlePhoneKeyDown,
    setAddress,
    setAddressError,
    setEmailConsent,
    setPhoneConsent,
    setPhoneError,
    address,
    addressError,
    selectedAddressDetails,
    hasValidAddress,
    hasValidPhone,
    hasPhoneError,
    emailConsent,
    phoneConsent,
    phoneError,
    phone,
  } = useProfileForm();

  // Pre-fill the address from the guest cache so a user who already
  // entered their address on the landing page (or in the in-chat
  // MobileAddressBar) doesn't have to retype it during signup. We do
  // this only once — guarded by ``hasPrefilledAddressRef`` — so if the
  // user clears or edits the field after sign-in the form respects
  // their input. ``handleAddressSelect`` is the same path Google Places
  // uses on a real selection: it marks the address as valid, parses
  // the components for the voting-analysis check, and lets the user
  // submit without re-picking. ``place_id`` + ``geometry`` are stubbed
  // because the guest cache only stores what the submission payload
  // needs (formatted address + address_components); the other fields
  // are unused by ``handleAddressSelect``'s validation path.
  const { address: guestAddress } = useGuestAddress();
  const hasPrefilledAddressRef = useRef(false);
  useEffect(() => {
    if (hasPrefilledAddressRef.current) return;
    if (!guestAddress?.formatted_address) return;
    // A county preview's address is a public building's, not the voter's home.
    if (isCountyPreview(guestAddress)) return;
    hasPrefilledAddressRef.current = true;
    setAddress(guestAddress.formatted_address);
    handleAddressSelect({
      formatted_address: guestAddress.formatted_address,
      place_id: '',
      address_components: (guestAddress.address_components ?? []) as any[],
      geometry: { location: { lat: 0, lng: 0 } },
    });
  }, [guestAddress, setAddress, handleAddressSelect]);

  const clearErrors = () => {
    setAddressError(null);
    setPhoneError(null);
    setFirstNameError(null);
    setLastNameError(null);
  };

  const handleSaveProfileClick = async () => {
    if (!user) {
      throw new Error('User not logged in.');
    }

    // Validate in on-screen order (name → address → phone) so focus lands on
    // the truly first invalid field (#1589).
    if (!firstName.trim()) {
      setFirstNameError(t('errorFirstName'));
      focusInvalidField(firstNameRef);
      return;
    }
    if (!lastName.trim()) {
      setLastNameError(t('errorLastName'));
      focusInvalidField(lastNameRef);
      return;
    }

    if (!hasValidAddress || !selectedAddressDetails) {
      setAddressError(t('errorSelectAddress'));
      focusInvalidField(addressRef);
      return;
    }

    if (hasPhoneError) {
      setPhoneError(t('errorInvalidPhone'));
      focusInvalidField(phoneRef);
      return;
    }

    clearErrors();
    setIsSaving(true);

    try {
      // Save address (required), email consent separately, and phone consent separately
      // Prepare the address payload
      const addressPayload = {
        formatted_address: selectedAddressDetails.formatted_address,
        address_components: selectedAddressDetails.address_components,
      };

      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();

      // Research collaboration metadata: if the user arrived via a
      // research-partner link and we still have those params in context,
      // persist them to the user record so subsequent (logged-in) visits
      // without the URL params can still tag conversations to this
      // participant.
      const signupBody: Record<string, unknown> = {
        email: user.email,
        first_name: trimmedFirstName,
        last_name: trimmedLastName,
        address_payload: addressPayload,
        phone_number: hasValidPhone ? phone : '',
        preferred_language: language,
        // Server timestamps + IP-stamps the consent record.
        consent: {
          email_marketing: emailConsent,
          phone_marketing: phoneConsent,
        },
      };
      if (researchCampaignCode) {
        signupBody.campaign_code = researchCampaignCode;
      }
      if (researchParticipantId) {
        signupBody.participant_id = researchParticipantId;
      }

      await query('/user/signup', {
        method: 'POST',
        body: JSON.stringify(signupBody),
      });

      // Optimistically reflect everything we know is true after a 200 from
      // /user/signup — name + initialSetupComplete — so the modal closes
      // immediately rather than waiting on a second /auth/callback round
      // trip. The background refresh below still runs to pick up any
      // server-side derivations (state/zip/county from PII components,
      // hubspot_contact_id link, etc.) but the user no longer sees a
      // spinner while it does.
      const resolvedName =
        trimmedFirstName || trimmedLastName
          ? `${trimmedFirstName} ${trimmedLastName}`.trim()
          : user.name;
      const finishSignup = () => {
        updateUser({
          name: resolvedName,
          first_name: trimmedFirstName,
          last_name: trimmedLastName,
          initialSetupComplete: true,
        });

        onSignupComplete();
        if (wantsMembership) openMembership('signup');
        // Fire-and-forget refresh for server-derived fields that the
        // optimistic update couldn't fill (state/zip/county, ballot data
        // hooks, hubspot_contact_id). No spinner gating on this — modal is
        // already closing thanks to the optimistic update above.
        refreshUserProfile().catch((err) => {
          console.error('refreshUserProfile failed:', err);
          showNotification(t('errorProfileLoad'), 'error');
          setIsSaving(false);
        });
      };

      // The account now exists. Before the modal closes, offer the optional
      // answer-style step — unless this voter already chose a style (as a
      // guest, carried over at sign-in). Both of its buttons finish sign-up.
      if (!answerStyleStore.getSnapshot().explicit) {
        setAnswerStyleStepDone(() => finishSignup);
      } else {
        finishSignup();
      }
      return; // don't reset isSaving on success
    } catch (error) {
      console.error('Failed to save user data:', error);
      showNotification(t('errorSaveFailed'), 'error');
      setIsSaving(false); // only reset on failure so user can retry
    }
  };
  const theme = useTheme();
  const styles = getSignupStyles(theme);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    const el = buttonRef.current;
    if (!el) return;
    if (isSaving) {
      el.style.cursor = 'default';
      el.style.pointerEvents = 'none';
      // Double RAF: first frame lets browser paint the idle state,
      // second frame applies the change so the transition has a "before" to animate from.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.width = '48px';
          el.style.minWidth = '48px';
          el.style.borderRadius = '50%';
          el.style.padding = '0';
        });
      });
      // After shape transition completes, drop the border so only the spinner shows
      const timer = setTimeout(() => {
        if (buttonRef.current) {
          buttonRef.current.style.border = 'none';
          buttonRef.current.style.background = 'transparent';
        }
      }, 450);
      return () => clearTimeout(timer);
    } else {
      el.style.removeProperty('cursor');
      el.style.removeProperty('pointer-events');
      el.style.removeProperty('width');
      el.style.removeProperty('min-width');
      el.style.removeProperty('border-radius');
      el.style.removeProperty('padding');
      el.style.removeProperty('border');
      el.style.removeProperty('background');
    }
  }, [isSaving]);

  if (answerStyleStepDone) {
    return (
      <Box sx={styles.formContent}>
        <AnswerStyleSignupStep titleId="signup-modal-title" onDone={answerStyleStepDone} />
      </Box>
    );
  }

  return (
    <Box sx={styles.formContent}>
      {/* Brand mark + welcome. The heading is visible and still names the
          dialog (the modal's ``aria-labelledby`` points at it), so a first-time
          voter reads what this form is for instead of meeting a bare stack of
          fields. Mirrors the mobile app's signup header. */}
      <Box sx={styles.header}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <VotemateLogo size={40} />
        </Box>
        <Typography component="h2" id="signup-modal-title" sx={styles.title}>
          {t('welcome')}
        </Typography>
        <Typography sx={styles.subtitle}>{t('addressPrompt')}</Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          width: '100%',
        }}
      >
        {/* Label above + placeholder inside, matching the address and phone
            fields (a floating MUI label read differently from the others). */}
        <Box sx={{ flex: 1, width: '100%' }}>
          <Typography component="label" htmlFor="signup-first-name" sx={styles.fieldLabel}>
            {t('firstName')}
            <Box component="span" aria-hidden="true" sx={styles.requiredMarker}>
              {' *'}
            </Box>
          </Typography>
          <TextField
            id="signup-first-name"
            inputRef={firstNameRef}
            required
            fullWidth
            variant="outlined"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder={t('firstNamePlaceholder')}
            inputProps={{ maxLength: 100 }}
            error={!!firstNameError}
            helperText={firstNameError}
            sx={styles.phoneField}
          />
        </Box>
        <Box sx={{ flex: 1, width: '100%' }}>
          <Typography component="label" htmlFor="signup-last-name" sx={styles.fieldLabel}>
            {t('lastName')}
            <Box component="span" aria-hidden="true" sx={styles.requiredMarker}>
              {' *'}
            </Box>
          </Typography>
          <TextField
            id="signup-last-name"
            inputRef={lastNameRef}
            required
            fullWidth
            variant="outlined"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={t('lastNamePlaceholder')}
            inputProps={{ maxLength: 100 }}
            error={!!lastNameError}
            helperText={lastNameError}
            sx={styles.phoneField}
          />
        </Box>
      </Box>

      <Box sx={styles.addressSection}>
        <Typography sx={styles.fieldLabel}>
          {t('addressLabel')}
          <Box component="span" aria-hidden="true" sx={styles.requiredMarker}>
            {' *'}
          </Box>
        </Typography>
        <AutoCompleteAddressInput
          value={address}
          onChange={handleAddressChange}
          onAddressSelect={handleAddressSelect}
          inputRef={addressRef}
          placeholder={t('addressPlaceholder')}
          ariaLabel={t('addressLabel')}
          required
          error={!!addressError}
          helperText={addressError}
        />
      </Box>

      <Box sx={{ width: '100%' }}>
        <Typography id="signup-language-label" sx={styles.fieldLabel}>
          {t('preferredLanguage')}
        </Typography>
        <Box sx={styles.languageField}>
          <LanguageSelector
            labelId="signup-language-label"
            value={language}
            onChange={(code) => {
              setLanguage(code);
              changeLanguage(code);
              analytics.track('Language Updated', { source: 'signup', language: code });
            }}
          />
        </Box>
      </Box>

      <Box sx={styles.phoneSection}>
        <Typography component="label" htmlFor="signup-phone" sx={styles.fieldLabel}>
          {t('phoneLabel')}
        </Typography>
        <TextField
          id="signup-phone"
          inputRef={phoneRef}
          fullWidth
          variant="outlined"
          value={phone}
          onChange={(e) => handlePhoneChange(e.target.value)}
          onKeyDown={handlePhoneKeyDown}
          type="tel"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 14 }}
          placeholder={t('phonePlaceholder')}
          sx={styles.phoneField}
          error={!!phoneError}
          helperText={phoneError}
        />
      </Box>

      {/* Communication Preferences Box */}
      <Box sx={styles.communicationContent}>
        <Box>
          <Typography sx={styles.communicationHeader}>{t('keepInTouch')}</Typography>
          <Typography sx={styles.privacyNote}>{t('privacyNote')}</Typography>
        </Box>
      </Box>

      <Box sx={styles.consentSection}>
        <FormControlLabel
          control={
            <Checkbox checked={emailConsent} onChange={(e) => setEmailConsent(e.target.checked)} />
          }
          label={
            <Typography sx={styles.consentLabel}>
              <strong>{t('email')}</strong>
              <br />
              {t('emailConsent')}
            </Typography>
          }
          sx={styles.consentCheckbox}
        />

        <FormControlLabel
          control={
            <Checkbox checked={phoneConsent} onChange={(e) => setPhoneConsent(e.target.checked)} />
          }
          label={
            <Typography sx={styles.consentLabel}>
              <strong>{t('textMessage')}</strong>
              <br />
              {t('phoneConsent')}
            </Typography>
          }
          sx={styles.consentCheckbox}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={wantsMembership}
              onChange={(e) => setWantsMembership(e.target.checked)}
            />
          }
          label={
            <Typography sx={styles.consentLabel}>
              <strong>{t('membershipOptIn')}</strong>
              <br />
              {t('membershipOptInDesc')}
            </Typography>
          }
          sx={styles.consentCheckbox}
        />
      </Box>

      {/* Terms Header Box */}
      <Paper elevation={0} sx={styles.termsPaper}>
        <Typography sx={styles.termsHeader}>
          <strong>{t('lastStep')}</strong>
        </Typography>
        {/* Terms List Outside Box */}
        <List sx={styles.termsList}>
          <ListItem>
            <Typography sx={styles.termsItem}>
              <Trans
                t={t}
                i18nKey="termsConfirmation"
                components={{
                  termsLink: (
                    <a
                      href={EXTERNAL_URLS.TERMS_OF_USE}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                  privacyLink: (
                    <a
                      href={EXTERNAL_URLS.PRIVACY_POLICY}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </Typography>
          </ListItem>
          <ListItem>
            <Typography sx={styles.termsItem}>{t('ballotResponsibility')}</Typography>
          </ListItem>
        </List>
      </Paper>

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Button
          ref={buttonRef}
          disableRipple
          onClick={isSaving ? undefined : handleSaveProfileClick}
          sx={{
            position: 'relative',
            height: '48px',
            width: '100%',
            minWidth: 0,
            padding: '12px 16px',
            borderRadius: BORDER_RADIUS,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 500,
            border: '2px solid transparent',
            background: gradientBorderBackground(
              semanticColors.surface,
              semanticGradients.borderGradient
            ),
            color: semanticColors.textBody,
            overflow: 'hidden',
            cursor: 'pointer',
            transition:
              'width 0.4s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s ease',
            '&:hover': {
              background: gradientBorderBackground(
                semanticColors.surfaceSubtle,
                semanticGradients.borderGradient
              ),
            },
          }}
        >
          <Box
            sx={{
              transition: 'opacity 0.15s ease',
              opacity: isSaving ? 0 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {t('signUp')}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isSaving ? 1 : 0,
              transition: 'opacity 0.2s ease 0.4s',
              pointerEvents: 'none',
            }}
          >
            <GradientCircularProgress size={36} thickness={2.5} />
          </Box>
        </Button>
      </Box>

      {!isSaving && (
        <Button variant="text" onClick={onGoBack} sx={styles.goBackButton}>
          {t('goBack')}
        </Button>
      )}
    </Box>
  );
};

export default AddRegisteredAddress;
